import $ from 'jquery'
import _ from 'lodash'
import KFocused from '../../core/mixins/focused/KFocused'
import KDevice from '../../core/utils/KDevice'
import KPopper, { PopperType, positions } from '../../core/mixins/popper/KPopper'
import ResizeAware from '../resize-aware'
import { getFirstComponentChild } from '../../core/utils/KVdom'
import { getCorrectEventName } from '../../core/utils'
import raf from 'raf'
import { window } from 'ssr-window'

const transitionEnd = getCorrectEventName(window, 'transitionend')

export default {
  name: 'KPopover',
  mixins: [KFocused, KPopper],
  props: {
    title: {
      type: String,
      default: '',
    },
    trigger: {
      // Override KPopper's trigger
      type: String,
      default: 'click',
      validator: (value) => ['click', 'focus', 'hover', 'manual'].indexOf(value) !== -1,
    },
    adjustOnChange: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      popperType: PopperType.POPOVER,
      references: [],
      popperWidth: '',
    }
  },
  computed: {
    popoverCss() {
      const css = {
        'km-popover': true,
        ...this.popperClassObjectLike,
      }

      if (this.size) {
        css[`is-${this.size}`] = true
      }
      if (!this.arrow) {
        css[`km-popover--no-arrow`] = true
      }

      return css
    },
  },
  watch: {
    value(value) {
      if (!value) {
        return
      }
      if (this.widthEqualToReference && this.$el) {
        this.popperWidth = this.$el.getBoundingClientRect().width
      }
    },
    references(references) {
      _.forEach(references, (reference) => {
        this.registerReference(reference)
      })
    },
  },
  mounted() {
    this.reference = this.reference || this.$refs.reference || this.$el
    this.registerReference(this.reference)
  },
  render() {
    if (!this.popperVM) {
      return
    }

    const title = this.$slots.title || this.title
    const content = this.$slots.default || this.content

    const reference = this.$refs.reference || this.$slots.reference || this.reference
    const renderReference = getFirstComponentChild(reference)

    if (this.popperVM) {
      const popperStyle = {}
      if (this.popperWidth) {
        popperStyle.width = this.popperWidth + 'px'
      }

      this.popperVM.popperId = this.popperId
      this.popperVM.node = (
        <transition
          onEnter={this.enter}
          onAfterEnter={this.afterEnter}
          onLeave={this.leave}
          onAfterLeave={this.afterLeave}
        >
          <div
            ref="popper"
            v-show={this.showPopper}
            style={popperStyle}
            class={this.popoverCss}
            onMouseenter={() => {
              this.opening = true
            }}
            onMouseleave={() => {
              if (this.trigger === 'hover') {
                this.hide()
              }
            }}
            onMousemove={() => {
              if (this.trigger === 'hover') {
                this.opening = true
              }
            }}
          >
            {
              this.adjustOnChange ? <ResizeAware onResize={this.handleResize} /> : null
            }
            <div class="km-popover__inner">
              {title ? <div class="km-popover__title">{title}</div> : null}
              {content}
            </div>
            {this.arrow ? <div
              x-arrow
              class="km-popover__arrow"
            /> : null}
          </div>
        </transition>
      )
    }

    return renderReference
  },
  methods: {
    updatePosition() {
      this.popperEngine && this.popperEngine.scheduleUpdate()
    },
    handleResize: _.debounce(function ({ width, height }) {
      this.updatePosition()

      this.$emit('resize', { width, height })
    }, 50),
    registerReference(reference) {
      if (reference && !reference.dataset) {
        return
      }

      if (!!reference.dataset.popover === true) {
        return
      }

      reference.dataset.popover = true
      this.reference = reference

      if (reference.nodeType === 1) {
        $(reference).attr({
          tabindex: 0,
          'aria-describedby': this.popperId,
        })

        this.bindTriggerEvents(reference)
      }
    },
    getTransformOrigin() {
      if (!this.popperData) {
        return
      }
      const placement = this.popperData.placement
      const positionMeta = positions[placement]

      const origin = positionMeta ? positionMeta.origin(this.popperData) : 'center center'
      return origin
    },
    getTransformTranslate() {
      const popperElm = this.popperEngine.popper
      const placement = popperElm.getAttribute('x-placement').split('-')[0]
      const targetTransform = popperElm.style.transform // translate3d(${foo}px, ${bar}px, 0px)

      const translateOffset = 10
      const translate = {
        top: targetTransform.replace(
          /translate3d\((\d+)px, (\d+)px, 0px\)/,
          (all, $1, $2) => `translate3d(${$1}px, ${+$2 + translateOffset}px, 0px)`,
        ),
        bottom: targetTransform.replace(
          /translate3d\((\d+)px, (\d+)px, 0px\)/,
          (all, $1, $2) => `translate3d(${$1}px, ${+$2 - translateOffset}px, 0px)`,
        ),
        left: targetTransform.replace(
          /translate3d\((\d+)px, (\d+)px, 0px\)/,
          (all, $1, $2) => `translate3d(${+$1 + translateOffset}px, ${$2}px, 0px)`,
        ),
        right: targetTransform.replace(
          /translate3d\((\d+)px, (\d+)px, 0px\)/,
          (all, $1, $2) => `translate3d(${+$1 - translateOffset}px, ${$2}px, 0px)`,
        ),
      }[placement]

      // return translate + ' scale(.1) translateZ(0)'
      return translate
    },
    enter(el, done) {
      if (KDevice.noAnim) {
        done()
        return
      }

      raf(() => {
        this.targetTransform = el.style.transform
        // el.style.transform = this.getTransformTranslate() + ' scale(.1)'
        el.style.transform = this.getTransformTranslate()
        el.style.transformOrigin = this.getTransformOrigin()
        el.style.opacity = 0
        el.style.transition = 'none'

        raf(() => {
          el.style.transform = this.targetTransform
          el.style.opacity = 1
          // el.style.transition = `transform .25s cubic-bezier(.39, 0, .45, 1.4), opacity .25s linear`
          el.style.transition = `transform .25s cubic-bezier(0.23, 1, 0.32, 1), opacity .25s linear`
          // el.style.transition = `transform .2s ease, opacity .2s linear`
          // el.style.transition = `transform 5s ease, opacity .25s linear`
          $(el).one(transitionEnd, () => {
            // el.style.transition = 'none'
            done()
          })
        })
      })
    },
    afterEnter(el) {
      el.style.transition = 'none'
    },
    leave(el, done) {
      if (KDevice.noAnim) {
        done()
        return
      }

      $(el).one(transitionEnd, () => {
        el.style.transition = 'none'
        done()
      })

      // el.style.transform = this.targetTransform + ' scale(.1)'
      el.style.transform = this.getTransformTranslate()
      el.style.transition = `transform .15s cubic-bezier(.25, .8, .25, 1), opacity .15s linear`
      el.style.opacity = 0
    },
    afterLeave() {
      this.destroyPopper()
    },
  },
}

const getReference = (el, binding, vnode) => {
  const popoverRef = binding.value || binding.arg

  const popper = vnode.context.$refs[popoverRef]
  // console.log('popoverRef: ', popoverRef, popper)
  if (popper) {
    popper.reference = el
    if (popper.references.indexOf(el) === -1) {
      popper.references.push(el)
    }
  }
}

export const directive = {
  name: 'popover',
  bind(el, binding, vnode) {
    getReference(el, binding, vnode)
    $(el)
      .on('mouseenter.popover.active', () => {
        getReference(el, binding, vnode)
      })
      .on('mousedown.popover.active', () => {
        getReference(el, binding, vnode)
      })
  },
  inserted(el, binding, vnode) {
    getReference(el, binding, vnode)
  },
}
